import { useCallback, useMemo, useState } from "react";
import { useActions, useSelector } from "app/utils/hooks";
import {
  clearGroup,
  clearMyInformationData,
  clearSelectedGroupData,
  fetchGroups,
} from "../../actions/groups";
import { RootState } from "typedefs";
import { useLocation } from "react-router-dom";
import { shallowEqual } from "react-redux";
import UserModel from "app/models/UserModel";
import { usePagination } from "app/utils/hooks/usePagination";
import { GroupsModel } from "app/models";
import useDebounce from "app/utils/hooks/useDebounce";

type QueryType = {
  page: number;
  perPage: number;
  s?: string;
};

const PER_PAGE = 80;
const INIT_PAGE = 1;

export const useGroupsModel = () => {
  const [searchTerm, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const location = useLocation();
  const groupActions = useActions({
    fetchGroups,
    clearGroup,
    clearSelectedGroupData,
    clearMyInformationData,
  });
  const user = useSelector(
    (state: RootState): UserModel => state.user.self,
    shallowEqual,
  );

  const canCreateGroup = user && user.planPermissions.includes("create_teams");

  const [isOpenCreateGroupModal, setIsOpenCreateGroupModal] = useState(false);

  const queryParams = new URLSearchParams(location.search);

  const createGroupModalHandler = (): void => {
    if (canCreateGroup) {
      setIsOpenCreateGroupModal((prev: boolean): boolean => !prev);
    }
  };

  const INIT_QUERY: QueryType = {
    page: INIT_PAGE,
    perPage: PER_PAGE,
    s: debouncedSearch || undefined,
  };

  const {
    data: groups,
    pending,
    sentryRef,
  } = usePagination<QueryType, GroupsModel>({
    initialParams: INIT_QUERY,
    fetchData: (params, signal) => groupActions.fetchGroups(params, signal),
    clearDataAction: () => {
      groupActions.clearSelectedGroupData();
      groupActions.clearMyInformationData();
      groupActions.clearGroup();
    },
    dependencies: [debouncedSearch],
  });

  useMemo((): void => {
    const createGroupParam = queryParams.get("create-group");

    if (createGroupParam && canCreateGroup) {
      createGroupModalHandler();
    }
  }, [location, canCreateGroup]);

  const onSearchGroup = useCallback((value: string) => {
    if (value != null && value.length > 0) {
      setSearch(value);
    } else {
      setSearch("");
    }
  }, []);

  return {
    searchTerm,
    onSearchGroup,
    canCreateGroup,
    groups,
    createGroupModalHandler,
    isOpenCreateGroupModal,
    pending,
    sentryRef,
  };
};
